import * as React from 'react';
import axios from 'axios';
import {
  Aggregate,
  AggregateCategories,
  AddonOption,
} from '../../Types/Aggregates';
import { Project } from '../../Types/Project';
import DataContext, { DataContextApi } from './context';

interface DataProviderProps {
  children: React.ReactNode;
}

export const DataProvider = ({ children }: DataProviderProps) => {
  const [showSideMenu, setShowSideMenu] = React.useState(false);
  const [projects, setProjects] = React.useState<Project[]>([]);
  const [aggregates, setAggregates] = React.useState<Aggregate[]>([]);
  const [activeUserInputs, setActiveUserInputs] = React.useState(
    window.localStorage.getItem('activeUserInputs')
      ? JSON.parse(window.localStorage.getItem('activeUserInputs') || '')
      : {}
  );
  const [
    activeAggregate,
    setActiveAggregate,
  ] = React.useState<Aggregate | null>(
    window.localStorage.getItem('activeAggregate')
      ? JSON.parse(window.localStorage.getItem('activeAggregate') || '')
      : null
  );

  const [latestCalcResults, setLatestCalcResults] = React.useState<
    AggregateCategories[]
  >([]);

  // Available addons for current aggregate/power supply combination
  const [availableAddons, setAvailableAddons] = React.useState<AddonOption[]>(
    []
  );

  // New: result lifecycle tracking
  const [selectedResultId, setSelectedResultIdState] = React.useState<
    string | null
  >(window.localStorage.getItem('selectedResultId') || null);

  const [alternativeTo, setAlternativeToState] = React.useState<string | null>(
    window.localStorage.getItem('alternativeTo') || null
  );

  const [hasUnsavedChanges, setHasUnsavedChanges] = React.useState<boolean>(
    false
  );

  // Persist selectedResultId to localStorage
  const setSelectedResultId = (id: string | null) => {
    setSelectedResultIdState(id);
    if (id) {
      window.localStorage.setItem('selectedResultId', id);
    } else {
      window.localStorage.removeItem('selectedResultId');
    }
  };

  // Persist alternativeTo to localStorage
  const setAlternativeTo = (id: string | null) => {
    setAlternativeToState(id);
    if (id) {
      window.localStorage.setItem('alternativeTo', id);
    } else {
      window.localStorage.removeItem('alternativeTo');
    }
  };

  // Mark state as saved (after successful save)
  const markAsSaved = () => {
    setHasUnsavedChanges(false);
  };

  // Start a new calculation (clears tracking, keeps inputs for now)
  const startNewCalculation = () => {
    setSelectedResultId(null);
    setAlternativeTo(null);
    setHasUnsavedChanges(false);
    // Note: we don't clear activeUserInputs/activeAggregate here
    // The caller (New Calculation button) should handle that separately
  };

  // Clear user-specific data that shouldn't persist across account changes
  const clearUserData = () => {
    setSelectedResultId(null);
    setAlternativeTo(null);
    setHasUnsavedChanges(false);
    // Optionally also clear inputs/aggregate if desired
    // setActiveUserInputs({});
    // setActiveAggregate(null);
  };

  // Handle user change - clear data if different user logs in
  const onUserChange = (userId: string | number | undefined) => {
    const cachedUserId = window.localStorage.getItem('cachedUserId');
    const newUserId = userId ? String(userId) : null;

    if (!newUserId) {
      // User logged out - clear user-specific data and cached ID
      clearUserData();
      window.localStorage.removeItem('cachedUserId');
      return;
    }

    if (!cachedUserId || cachedUserId !== newUserId) {
      // No cached user (fresh start or cleared) or different user - clear stale data
      clearUserData();
    }

    // Update cached user ID
    window.localStorage.setItem('cachedUserId', newUserId);
  };

  const toggleSideMenu = () => {
    setShowSideMenu(!showSideMenu);
  };

  React.useEffect(() => {
    if (activeAggregate && Object.keys(activeAggregate)?.length) {
      window.localStorage.setItem(
        'activeAggregate',
        JSON.stringify(activeAggregate)
      );
    }
  }, [activeAggregate]);

  React.useEffect(() => {
    // for testing
    if (activeUserInputs && Object.keys(activeUserInputs)?.length) {
      window.localStorage.setItem(
        'activeUserInputs',
        JSON.stringify(activeUserInputs)
      );
    }
  }, [activeUserInputs]);

  // Track unsaved changes when inputs or aggregate change
  const wrappedSetActiveUserInputs = (inputs: any) => {
    setActiveUserInputs(inputs);
    // Mark as unsaved if we have any inputs
    if (inputs && Object.keys(inputs).length > 0) {
      setHasUnsavedChanges(true);
    }
  };

  const wrappedSetActiveAggregate = (aggregate: Aggregate | null) => {
    // Clear availableAddons when switching to a different aggregate
    // They'll be re-fetched, and addon resolution will filter out any
    // that aren't valid for the new aggregate (some addons work across aggregates)
    if (aggregate && activeAggregate && aggregate.id !== activeAggregate.id) {
      setAvailableAddons([]);
    }

    setActiveAggregate(aggregate);
    // Mark as unsaved when aggregate changes (but not when clearing)
    if (aggregate) {
      setHasUnsavedChanges(true);
    }
  };

  // Load saved result data without triggering unsaved changes
  const loadSavedResultData = (inputs: any, aggregate: Aggregate | null) => {
    setActiveUserInputs(inputs);
    setActiveAggregate(aggregate);
    setHasUnsavedChanges(false);
  };

  // Fetch available addons for a given aggregate and power supply
  const fetchAvailableAddons = async (
    aggregateId: number | string,
    powerSupply: string
  ): Promise<AddonOption[]> => {
    try {
      const { status, data } = await axios.get(`/addons/`, {
        params: {
          aggregate_id: aggregateId,
          power_supply: powerSupply,
        },
      });
      if (status === 200) {
        setAvailableAddons(data);
        return data;
      }
    } catch (error) {
      // Error fetching addons - fail silently
    }
    return [];
  };

  const resetStorageData = () => {
    /* setActiveUserInputs({});
    window.localStorage.setItem('activeUserInputs', ''); */
    window.localStorage.setItem('activeAggregate', '');
  };

  return (
    <DataContext.Provider
      value={{
        toggleSideMenu,
        showSideMenu,
        projects,
        activeUserInputs,
        setActiveUserInputs: wrappedSetActiveUserInputs,
        activeAggregate,
        setActiveAggregate: wrappedSetActiveAggregate,
        resetStorageData,
        setProjects,
        aggregates,
        setAggregates,
        latestCalcResults,
        setLatestCalcResults,
        // New: result lifecycle tracking
        selectedResultId,
        setSelectedResultId,
        alternativeTo,
        setAlternativeTo,
        hasUnsavedChanges,
        markAsSaved,
        startNewCalculation,
        clearUserData,
        onUserChange,
        loadSavedResultData,
        availableAddons,
        setAvailableAddons,
        fetchAvailableAddons,
      }}
    >
      {children}
    </DataContext.Provider>
  );
};

export const useData = (): DataContextApi => React.useContext(DataContext);
